import { create } from "zustand";
import {
  AutocompleteGameCard,
  FinishEnum,
  NONFOIL_FINISH,
} from "modules/cards/model/card";
import { TradeCard } from "modules/swap-calculator/model/tradeCard";
import { GameId } from "../../common/model/models";
import { CardPriceCurrenciesEnum } from "modules/cards/model/card-price";
import { priceForFinish } from "modules/cards/model/finishBehavior";

interface ModalState {
  open: boolean;
  card: TradeCard;
}

interface SwapCalculatorStore {
  // Card lists
  leftCards: TradeCard[];
  rightCards: TradeCard[];

  // Form state - internalized from cards module for swap calculator use
  selectedCard: AutocompleteGameCard | null;
  finish: FinishEnum;
  quantity: number;
  autocompleteKey: string;

  // Modal state
  modal: ModalState;
  removingCards: Set<string>;

  // List actions
  addCardToLeft: (card: TradeCard) => void;
  addCardToRight: (card: TradeCard) => void;
  updateCard: (
    side: "left" | "right",
    index: number,
    updates: Partial<TradeCard>
  ) => void;
  removeCardById: (cardId: string, side: "left" | "right") => void;
  startRemovingCard: (cardId: string) => void;
  completeRemoval: (cardId: string) => void;

  // Form actions
  setSelectedCard: (card: AutocompleteGameCard | null) => void;
  setFinish: (finish: FinishEnum) => void;
  setQuantity: (quantity: number) => void;
  setAutocompleteKey: (key: string) => void;
  resetForm: () => void;

  // Autocomplete reset callback (registered by autocomplete component)
  autocompleteResetCallback: (() => void) | null;
  registerAutocompleteReset: (callback: () => void) => void;
  unregisterAutocompleteReset: () => void;
  resetAutocomplete: () => void;

  // Modal actions
  openModal: (card: TradeCard) => void;
  closeModal: () => void;

  // Reset actions
  reset: () => void;
}

const DEFAULT_SELECTED_CARD: AutocompleteGameCard = {
  id: "",
  name: "",
  slug: "",
  isPromo: false,
  number: "",
  price: { default: 0, foil: 0, currency: CardPriceCurrenciesEnum.EUR },
  language: "",
  costPrinted: "",
  keyruneCode: "",
  setCode: "",
  finishes: [],
  game: "" as GameId,
};

const DEFAULT_TRADE_CARD: TradeCard = {
  id: "",
  name: "",
  number: "",
  price: { default: 0, foil: 0, currency: CardPriceCurrenciesEnum.EUR },
  costPrinted: "",
  setCode: "",
  finishes: [],
  finish: NONFOIL_FINISH,
  quantity: 1,
  index: 0,
  customPrice: 0,
  game: "" as GameId,
};

const DEFAULT_MODAL_STATE: ModalState = {
  open: false,
  card: DEFAULT_TRADE_CARD,
};

export const useSwapCalculatorStore = create<SwapCalculatorStore>(
  (set, get) => ({
    // Initial state
    leftCards: [],
    rightCards: [],
    selectedCard: null,
    finish: NONFOIL_FINISH,
    quantity: 1,
    autocompleteKey: "",
    autocompleteResetCallback: null,
    modal: DEFAULT_MODAL_STATE,
    removingCards: new Set<string>(),

    // List actions
    addCardToLeft: (card) =>
      set((state) => ({
        leftCards: [
          ...state.leftCards,
          { ...card, list: "left", index: state.leftCards.length },
        ],
      })),

    addCardToRight: (card) =>
      set((state) => ({
        rightCards: [
          ...state.rightCards,
          { ...card, list: "right", index: state.rightCards.length },
        ],
      })),

    updateCard: (side, index, updates) =>
      set((state) => {
        const cards = side === "left" ? state.leftCards : state.rightCards;
        const updatedCards = [...cards];
        updatedCards[index] = { ...updatedCards[index], ...updates };

        return side === "left"
          ? { leftCards: updatedCards }
          : { rightCards: updatedCards };
      }),

    removeCardById: (cardId, side) =>
      set((state) => {
        const cards = side === "left" ? state.leftCards : state.rightCards;
        const filteredCards = cards.filter((card) => card.id !== cardId);

        return side === "left"
          ? { leftCards: filteredCards }
          : { rightCards: filteredCards };
      }),

    startRemovingCard: (cardId) =>
      set((state) => ({
        removingCards: new Set(state.removingCards).add(cardId),
      })),

    completeRemoval: (cardId) =>
      set((state) => {
        const next = new Set(state.removingCards);
        next.delete(cardId);
        return { removingCards: next };
      }),

    // Form actions
    setSelectedCard: (card) => set({ selectedCard: card }),

    setFinish: (finish) => set({ finish }),

    setQuantity: (quantity) => set({ quantity }),

    setAutocompleteKey: (key) => set({ autocompleteKey: key }),

    resetForm: () =>
      set({
        selectedCard: null,
        finish: NONFOIL_FINISH,
        quantity: 1,
      }),

    // Autocomplete reset callback actions
    registerAutocompleteReset: (callback) =>
      set({ autocompleteResetCallback: callback }),

    unregisterAutocompleteReset: () => set({ autocompleteResetCallback: null }),

    resetAutocomplete: () => {
      const callback = get().autocompleteResetCallback;
      if (callback) {
        callback();
      }
    },

    // Modal actions
    openModal: (card) =>
      set({
        modal: { open: true, card },
      }),

    closeModal: () =>
      set({
        modal: DEFAULT_MODAL_STATE,
      }),

    // Reset actions
    reset: () => {
      // Call autocomplete reset before clearing state
      const callback = get().autocompleteResetCallback;
      if (callback) {
        callback();
      }
      set({
        leftCards: [],
        rightCards: [],
        selectedCard: null,
        finish: NONFOIL_FINISH,
        quantity: 1,
        autocompleteKey: "",
        modal: DEFAULT_MODAL_STATE,
        removingCards: new Set<string>(),
      });
    },
  })
);

// ============================================================================
// SELECTOR HOOKS - Optimal Zustand patterns with shallow equality
// ============================================================================

/**
 * Form state selector - for autocomplete and input components
 * Split into separate selectors to avoid shallow equality issues with objects/functions
 */
export const useSwapFormState = () => {
  const selectedCard = useSwapCalculatorStore((state) => state.selectedCard);
  const finish = useSwapCalculatorStore((state) => state.finish);
  const quantity = useSwapCalculatorStore((state) => state.quantity);
  const autocompleteKey = useSwapCalculatorStore(
    (state) => state.autocompleteKey
  );
  const setSelectedCard = useSwapCalculatorStore(
    (state) => state.setSelectedCard
  );
  const setFinish = useSwapCalculatorStore((state) => state.setFinish);
  const setQuantity = useSwapCalculatorStore((state) => state.setQuantity);
  const setAutocompleteKey = useSwapCalculatorStore(
    (state) => state.setAutocompleteKey
  );
  const resetForm = useSwapCalculatorStore((state) => state.resetForm);
  const resetAutocomplete = useSwapCalculatorStore(
    (state) => state.resetAutocomplete
  );

  return {
    selectedCard,
    finish,
    quantity,
    autocompleteKey,
    setSelectedCard,
    setFinish,
    setQuantity,
    setAutocompleteKey,
    resetForm,
    resetAutocomplete,
  };
};

/**
 * Card lists selector - for list containers and action bar
 * Split into separate selectors to avoid shallow equality issues
 */
export const useSwapLists = () => {
  const leftCards = useSwapCalculatorStore((state) => state.leftCards);
  const rightCards = useSwapCalculatorStore((state) => state.rightCards);
  const addCardToLeft = useSwapCalculatorStore((state) => state.addCardToLeft);
  const addCardToRight = useSwapCalculatorStore(
    (state) => state.addCardToRight
  );
  const reset = useSwapCalculatorStore((state) => state.reset);

  return { leftCards, rightCards, addCardToLeft, addCardToRight, reset };
};

/**
 * Modal state selector - for modal components
 * Split into separate selectors to avoid shallow equality issues
 */
export const useSwapModal = () => {
  const modal = useSwapCalculatorStore((state) => state.modal);
  const openModal = useSwapCalculatorStore((state) => state.openModal);
  const closeModal = useSwapCalculatorStore((state) => state.closeModal);

  return { modal, openModal, closeModal };
};

/**
 * Card editing selector - for edit modal and card updates
 * Split into separate selectors to avoid shallow equality issues
 */
export const useSwapCardEdit = () => {
  const leftCards = useSwapCalculatorStore((state) => state.leftCards);
  const rightCards = useSwapCalculatorStore((state) => state.rightCards);
  const modal = useSwapCalculatorStore((state) => state.modal);
  const updateCard = useSwapCalculatorStore((state) => state.updateCard);
  const removeCardById = useSwapCalculatorStore(
    (state) => state.removeCardById
  );
  const openModal = useSwapCalculatorStore((state) => state.openModal);
  const closeModal = useSwapCalculatorStore((state) => state.closeModal);

  return {
    leftCards,
    rightCards,
    modal,
    updateCard,
    removeCardById,
    openModal,
    closeModal,
  };
};

/**
 * Card removal animation selector
 * Split into separate selectors to avoid shallow equality issues
 */
export const useSwapRemoval = () => {
  const removingCards = useSwapCalculatorStore((state) => state.removingCards);
  const startRemovingCard = useSwapCalculatorStore(
    (state) => state.startRemovingCard
  );
  const completeRemoval = useSwapCalculatorStore(
    (state) => state.completeRemoval
  );

  return { removingCards, startRemovingCard, completeRemoval };
};

/**
 * Computed values selector - for trade totals and differences
 * Split into separate selectors to avoid any equality comparison issues
 */
export const useSwapCalculatorComputed = () => {
  const leftCards = useSwapCalculatorStore((state) => state.leftCards);
  const rightCards = useSwapCalculatorStore((state) => state.rightCards);
  const selectedCard = useSwapCalculatorStore((state) => state.selectedCard);
  const finish = useSwapCalculatorStore((state) => state.finish);
  const quantity = useSwapCalculatorStore((state) => state.quantity);

  // Compute totals
  const leftTotal = leftCards.reduce(
    (acc, card) => acc + card.customPrice * card.quantity,
    0
  );

  const rightTotal = rightCards.reduce(
    (acc, card) => acc + card.customPrice * card.quantity,
    0
  );

  const difference = Math.abs(leftTotal - rightTotal);

  // Compute current trade card from selected autocomplete card
  const currentTradeCard: TradeCard | null = selectedCard
    ? {
        name: selectedCard.name,
        id: selectedCard.id,
        printingKey: selectedCard.printingKey,
        setCode: selectedCard.setCode,
        number: selectedCard.number,
        price: selectedCard.price,
        quantity: quantity,
        finish: finish,
        finishes: selectedCard.finishes,
        costPrinted: selectedCard.costPrinted,
        customPrice: priceForFinish(selectedCard.price, finish),
        game: selectedCard.game,
      }
    : null;

  return {
    leftTotal,
    rightTotal,
    difference,
    currentTradeCard,
    isLeftHigher: leftTotal > rightTotal,
    isRightHigher: rightTotal > leftTotal,
  };
};
